import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  RefreshCw,
  FileSpreadsheet,
  Database,
  Clock,
  Lock,
  AlertTriangle,
  Settings,
  GitMerge,
  Eye,
  CheckCircle,
  XCircle
} from 'lucide-react';

/**
 * Routing Data Sources Component
 * Shows configured sync sources from admin and allows users to refresh data
 */
export default function RoutingSheetImporter() {
  const [syncConfigs, setSyncConfigs] = useState([]);
  const [derivedViews, setDerivedViews] = useState([]);
  const [loadingConfigs, setLoadingConfigs] = useState(true);
  const [refreshingId, setRefreshingId] = useState(null);
  const [refreshMessage, setRefreshMessage] = useState(null);

  const db = getFirestore();

  // Load existing sync configurations and derived views
  useEffect(() => {
    loadSyncConfigs();
    loadDerivedViews();
  }, []);

  const loadSyncConfigs = async () => {
    try {
      setLoadingConfigs(true);
      const configsRef = collection(db, 'routing_sync_configs');
      const snapshot = await getDocs(configsRef);

      const configs = [];
      snapshot.forEach(doc => {
        configs.push({ id: doc.id, ...doc.data() });
      });

      // Sort by name
      configs.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setSyncConfigs(configs);
    } catch (error) {
      console.error('Error loading sync configs:', error);
    } finally {
      setLoadingConfigs(false);
    }
  };

  const loadDerivedViews = async () => {
    try {
      const viewsRef = collection(db, 'routing_derived_views');
      const snapshot = await getDocs(viewsRef);

      const views = [];
      snapshot.forEach(doc => {
        views.push({ id: doc.id, ...doc.data() });
      });

      // Sort by name and filter enabled only
      views.sort((a, b) => (a.viewName || '').localeCompare(b.viewName || ''));
      setDerivedViews(views.filter(v => v.enabled));
    } catch (error) {
      console.error('Error loading derived views:', error);
    }
  };

  // Refresh data for an existing sync config
  const refreshConfig = async (config) => {
    try {
      setRefreshingId(config.id);
      setRefreshMessage(null);

      const functions = getFunctions();
      const syncFunction = httpsCallable(functions, 'syncGoogleSheetsManual');

      const result = await syncFunction({
        sheetId: config.sheetId,
        tabName: config.tabName,
        tableName: config.tableName,
        configId: config.id
      });

      if (result.data?.schemaBlocked) {
        setRefreshMessage({
          type: 'warning',
          configId: config.id,
          text: `Schema change detected. Contact admin to approve changes.`
        });
        await loadSyncConfigs();
        return;
      }

      if (result.data?.success) {
        setRefreshMessage({
          type: 'success',
          configId: config.id,
          text: `Refreshed ${result.data.rowsSynced} rows`
        });
        await loadSyncConfigs();
      } else {
        throw new Error(result.data?.error || 'Refresh failed');
      }
    } catch (error) {
      console.error('Refresh error:', error);
      setRefreshMessage({
        type: 'error',
        configId: config.id,
        text: error.message || 'Refresh failed'
      });
    } finally {
      setRefreshingId(null);
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'Never';
    // Handle Firestore Timestamp objects
    const date = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
    if (isNaN(date.getTime())) return 'Never';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Loading state
  if (loadingConfigs) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  // Empty state - no configs from admin
  if (syncConfigs.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-dashed border-slate-300 p-8 text-center">
        <Database size={48} className="mx-auto text-slate-300 mb-4" />
        <h3 className="text-lg font-medium text-slate-500">No Data Sources Configured</h3>
        <p className="text-slate-400 mt-2">
          Contact an admin to configure Google Sheets sync sources.
        </p>
        <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-400">
          <Settings size={14} />
          <span>Admin → Routing Sync</span>
        </div>
      </div>
    );
  }

  // Show configured sync sources
  const enabledConfigs = syncConfigs.filter(c => c.enabled);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-slate-800">Data Sources</h3>
        <p className="text-sm text-slate-500">Click Refresh to update data from Google Sheets</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {enabledConfigs.map((config) => (
          <div
            key={config.id}
            className={`bg-white rounded-xl border overflow-hidden hover:shadow-md transition-shadow ${
              config.pendingSchemaChange?.requiresApproval
                ? 'border-amber-300'
                : 'border-slate-200'
            }`}
          >
            {/* Card Header */}
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database size={18} className="text-blue-600" />
                <span className="font-medium text-slate-800 text-sm">{config.tableName || config.name}</span>
              </div>
              <div className="flex items-center gap-2">
                {config.lockedSchema && (
                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <Lock size={10} />
                    {config.lockedSchema.columns?.length} cols
                  </span>
                )}
                {config.pendingSchemaChange?.requiresApproval && (
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <AlertTriangle size={10} />
                    Blocked
                  </span>
                )}
              </div>
            </div>

            {/* Card Body */}
            <div className="p-4 space-y-3">
              <div className="text-xs text-slate-500 space-y-1">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet size={12} />
                  <span className="truncate">{config.tabName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Database size={12} />
                  <span className="font-mono">{config.tableName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={12} />
                  <span>Last sync: {formatDate(config.lastSync)}</span>
                </div>
                {config.lastSyncRows && (
                  <div className="text-slate-400">
                    {config.lastSyncRows.toLocaleString()} rows
                  </div>
                )}
              </div>

              {/* Message for this config */}
              {refreshMessage?.configId === config.id && (
                <div className={`p-2 rounded text-xs ${
                  refreshMessage.type === 'success' ? 'bg-green-50 text-green-700' :
                  refreshMessage.type === 'warning' ? 'bg-amber-50 text-amber-700' :
                  'bg-red-50 text-red-700'
                }`}>
                  {refreshMessage.text}
                </div>
              )}

              {/* Refresh Button or Blocked Message */}
              {config.pendingSchemaChange?.requiresApproval ? (
                <div className="p-2 bg-amber-50 rounded-lg text-xs text-amber-700">
                  Schema change detected. Contact admin to approve.
                </div>
              ) : (
                <button
                  onClick={() => refreshConfig(config)}
                  disabled={refreshingId === config.id}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {refreshingId === config.id ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={14} />
                      Refresh Data
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Derived Views Section */}
      {derivedViews.length > 0 && (
        <div className="mt-8">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-slate-800 flex items-center gap-2">
              <GitMerge size={20} className="text-purple-600" />
              Derived Views
            </h3>
            <p className="text-sm text-slate-500">Combined/transformed data views (auto-updated)</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {derivedViews.map((view) => (
              <div
                key={view.id}
                className="bg-white rounded-xl border border-purple-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Card Header */}
                <div className="px-4 py-3 bg-purple-50 border-b border-purple-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye size={18} className="text-purple-600" />
                    <span className="font-medium text-slate-800 text-sm">{view.viewName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {view.lastRefreshStatus === 'success' && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <CheckCircle size={10} />
                        Active
                      </span>
                    )}
                    {view.lastRefreshStatus === 'error' && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <XCircle size={10} />
                        Error
                      </span>
                    )}
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-4 space-y-3">
                  {view.description && (
                    <p className="text-sm text-slate-600">{view.description}</p>
                  )}

                  <div className="text-xs text-slate-500 space-y-1">
                    <div className="flex items-center gap-2">
                      <Database size={12} />
                      <span className="font-mono">inmocean_raw.{view.viewName}</span>
                    </div>
                    {view.sourceTables?.length > 0 && (
                      <div className="flex items-center gap-2">
                        <GitMerge size={12} />
                        <span>Sources: {view.sourceTables.join(', ')}</span>
                      </div>
                    )}
                    {view.lastRefresh && (
                      <div className="flex items-center gap-2">
                        <Clock size={12} />
                        <span>Updated: {formatDate(view.lastRefresh)}</span>
                      </div>
                    )}
                  </div>

                  {/* Auto-refresh indicator */}
                  {view.autoRefreshAfterSync && (
                    <div className="p-2 bg-purple-50 rounded-lg text-xs text-purple-700 flex items-center gap-2">
                      <RefreshCw size={12} />
                      Auto-updates when source tables sync
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help text */}
      {(enabledConfigs.length > 0 || derivedViews.length > 0) && (
        <div className="text-xs text-slate-400 flex items-center gap-2 mt-4">
          <Settings size={12} />
          <span>Data sources and views are managed by admins in Admin → Routing Sync</span>
        </div>
      )}
    </div>
  );
}
